import { Menu } from "@base-ui/react/menu"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Action } from "shared/types/Action"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import "shared/ui/menu/styles/_menu.scss"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { useProjectActions } from "features/project-management/model/useProjectActions"
import { ProjectScopedActionHandlers } from "features/project-management/types/ProjectActionHandlers"

const collisionAvoidance: Menu.Positioner.Props["collisionAvoidance"] = {
  align: "flip",
  side: "flip"
}

export interface CreateProjectElementButtonProps extends ButtonProps {
  onNewProduct?: ProjectScopedActionHandlers["onNewProduct"]
  onNewRoom?: ProjectScopedActionHandlers["onNewRoom"]
  onNewFloor?: ProjectScopedActionHandlers["onNewFloor"]
}

export const CreateProjectElementButton = React.memo(
  ({
    onNewProduct,
    onNewRoom,
    onNewFloor,
    ...buttonProps
  }: CreateProjectElementButtonProps) => {
    const { t } = useTranslation()
    const { newProductAction, newRoomAction, newFloorAction } =
      useProjectActions({
        idPrefix: "create-project-element",
        onNewProduct,
        onNewRoom,
        onNewFloor
      })

    const actions = useMemo<Action[]>(
      () =>
        [newProductAction, newRoomAction, newFloorAction].filter(
          action => !!action.onClick
        ),
      [newProductAction, newRoomAction, newFloorAction]
    )

    if (actions.length === 0) return null

    return (
      <Menu.Root>
        <Tooltip content={t("Project.Actions.CreateElement")}>
          <Menu.Trigger
            render={
              <Button
                variant={ButtonVariant.PRIMARY}
                icon={IconName.PLUS_CIRCLE}
                isIconOnly
                label={t("Project.Actions.CreateElement")}
                {...buttonProps}
              />
            }
          />
        </Tooltip>
        <Menu.Portal>
          <Menu.Positioner
            className="menu__positioner"
            align="center"
            side="right"
            sideOffset={16}
            positionMethod="fixed"
            collisionAvoidance={collisionAvoidance}
          >
            <Menu.Popup className="menu__popup">
              {actions.map(({ isDanger, onClick, ...action }) => (
                <React.Fragment key={action.id}>
                  <Menu.Item
                    {...action}
                    onClick={onClick as React.MouseEventHandler<HTMLElement>}
                    className={classNames("btn", "btn-menu", "menu__item", {
                      "btn--danger": isDanger
                    })}
                  >
                    {action.icon ? <Icon name={action.icon} /> : null}
                    <span>{action.label}</span>
                  </Menu.Item>
                  <Menu.Separator className="menu__separator" />
                </React.Fragment>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    )
  }
)
